import { useState, useCallback, useRef } from 'react'
import type { AttentionLevel, Task } from '@/src/renderer/lib/db/models'
import { db } from '@/src/renderer/lib/db/indexeddb'
import { scheduleSync } from '@/src/renderer/lib/sync/sync-engine'
import { useSoftRemoveSound, useSoftAddSound } from '@/src/renderer/lib/sounds'
import type { DurationValue, TaskEntryMode, TaskEntryData, TaskCreationData } from '../TaskEntry.types'
import type { StartValue } from '../../WhenDropdown'
import type { DeadlineValue } from '../../DeadlineDropdown'

interface UseTaskEntryFormArgs {
  mode: TaskEntryMode
  task?: TaskEntryData
  resetKey?: number
  selected?: boolean
  onClick?: (taskId: string, event: React.MouseEvent) => void
  onCancel?: () => void
  onSubmitCreate?: (data: TaskCreationData) => void
  onSubmitEdit?: (taskId: string, data: TaskCreationData) => void
  onSubmitCreateAndContinue?: (data: TaskCreationData) => void
  onSubmitEditAndCreateBelow?: (taskId: string, data: TaskCreationData) => void
  /** Optional optimistic-update sink. When provided, `saveCollapsedToDb`
   *  routes badge updates through this callback instead of writing
   *  directly to Dexie. The host page (e.g. UpcomingPage) uses it to
   *  apply an optimistic UI update so the badge change is visible in the
   *  next paint instead of waiting on the IDB → LiveQuery → re-render
   *  roundtrip (~1–2s). Falls back to direct DB write when absent. */
  onContextMenuBadgeUpdate?: (taskId: string, updates: Partial<Omit<Task, 'id'>>) => void
}

export function useTaskEntryForm({
  mode, task, resetKey, selected, onClick,
  onCancel, onSubmitCreate, onSubmitEdit,
  onSubmitCreateAndContinue, onSubmitEditAndCreateBelow,
  onContextMenuBadgeUpdate,
}: UseTaskEntryFormArgs) {
  const isExpanded = mode === 'expanded' || mode === 'creating'
  const playSoftRemove = useSoftRemoveSound()
  const playSoftAdd = useSoftAddSound()

  // ── Form state ──
  const [title, setTitle] = useState(() => mode === 'expanded' && task ? task.title : '')
  const [notes, setNotes] = useState(() => mode === 'expanded' && task ? (task.description ?? '') : '')
  const [checked, setChecked] = useState(() => mode === 'expanded' && task ? task.completed : false)
  const [attention, setAttention] = useState<AttentionLevel | null>(() => mode === 'expanded' && task ? (task.attention ?? null) : null)
  const [duration, setDuration] = useState<DurationValue | null>(() => {
    if (mode === 'expanded' && task?.estimatedDuration) {
      return { minutes: task.estimatedDuration, mode: task.durationMode ?? 'duration' }
    }
    return null
  })
  const [tags, setTags] = useState<string[]>(() => mode === 'expanded' && task ? (task.tags ?? []) : [])
  const [start, setStart] = useState<StartValue | null>(() => {
    if (mode === 'expanded' && task?.startDate) {
      return { date: task.startDate, time: task.startTime ?? null, period: task.startPeriod ?? null }
    }
    return null
  })
  const [deadline, setDeadline] = useState<DeadlineValue | null>(() => {
    if (mode === 'expanded' && task?.dueDate) {
      return { date: task.dueDate, time: task.dueTime ?? null, period: task.duePeriod ?? null }
    }
    return null
  })

  // ── DB persistence ──
  const saveToDb = useCallback(async (updates: Partial<Omit<Task, 'id'>>) => {
    if (mode !== 'expanded' || !task) return
    // If the host wired an optimistic-update sink, route through it
    // (mirrors `saveCollapsedToDb` below). The sink is responsible
    // for both showing the change instantly AND persisting it.
    if (onContextMenuBadgeUpdate) {
      onContextMenuBadgeUpdate(task.id, updates)
      return
    }
    try {
      await db.updateTask(task.id, updates)
      scheduleSync()
    } catch (err) {
      console.error('[TaskEntry] Failed to save task update:', err)
    }
  }, [mode, task, onContextMenuBadgeUpdate])

  const saveCollapsedToDb = useCallback(async (updates: Partial<Omit<Task, 'id'>>) => {
    if (!task) return
    // If the host wired an optimistic-update sink, use it. The sink is
    // responsible for both showing the change instantly AND persisting
    // it to Dexie. Skipping our local write avoids a double round-trip.
    if (onContextMenuBadgeUpdate) {
      onContextMenuBadgeUpdate(task.id, updates)
      return
    }
    try {
      await db.updateTask(task.id, updates)
      scheduleSync()
    } catch (err) {
      console.error('[TaskEntry] Failed to save collapsed task update:', err)
    }
  }, [task, onContextMenuBadgeUpdate])

  const selectOnDropdownOpen = useCallback((open: boolean) => {
    if (open && !selected && task) {
      // Minimal stub — handleSelect only reads metaKey/shiftKey/ctrlKey for multi-select
      onClick?.(task.id, { metaKey: false, shiftKey: false, ctrlKey: false } as React.MouseEvent)
    }
  }, [selected, task, onClick])

  // ── Change handlers ──
  const handleAttentionChange = useCallback((value: AttentionLevel | null) => {
    if (value !== null && attention === null) playSoftAdd()
    setAttention(value)
    saveToDb({ attention: value })
  }, [saveToDb, playSoftAdd, attention])

  const handleDurationChange = useCallback((value: DurationValue | null) => {
    if (value !== null && duration === null) playSoftAdd()
    setDuration(value)
    saveToDb({
      estimatedDuration: value?.minutes ?? null,
      durationMode: value?.mode ?? null,
    })
  }, [saveToDb, playSoftAdd, duration])

  const handleTagsChange = useCallback((value: string[]) => {
    if (value.length > tags.length) playSoftAdd()
    setTags(value)
    saveToDb({ tags: value })
  }, [saveToDb, tags, playSoftAdd])

  const handleStartChange = useCallback((value: StartValue | null) => {
    if (value !== null && start === null) playSoftAdd()
    setStart(value)
    saveToDb({
      startDate: value?.date ?? null,
      startTime: value?.time ?? null,
      startPeriod: value?.period ?? null,
    })
  }, [saveToDb, playSoftAdd, start])

  const handleDeadlineChange = useCallback((value: DeadlineValue | null) => {
    if (value !== null && deadline === null) playSoftAdd()
    setDeadline(value)
    saveToDb({
      dueDate: value?.date ?? null,
      dueTime: value?.time ?? null,
      duePeriod: value?.period ?? null,
    })
  }, [saveToDb, playSoftAdd, deadline])

  // ── Form state initialization on mode change ──
  const formInitModeRef = useRef(mode)
  if (formInitModeRef.current !== mode) {
    formInitModeRef.current = mode
    if (mode === 'expanded' && task) {
      setTitle(task.title)
      setNotes(task.description ?? '')
      setChecked(task.completed)
      setAttention(task.attention ?? null)
      setDuration(task.estimatedDuration ? { minutes: task.estimatedDuration, mode: task.durationMode ?? 'duration' } : null)
      setTags(task.tags ?? [])
      setStart(task.startDate ? { date: task.startDate, time: task.startTime ?? null, period: task.startPeriod ?? null } : null)
      setDeadline(task.dueDate ? { date: task.dueDate, time: task.dueTime ?? null, period: task.duePeriod ?? null } : null)
    }
    if (mode === 'creating') {
      setTitle(''); setNotes(''); setChecked(false); setAttention(null)
      setDuration(null); setTags([]); setStart(null); setDeadline(null)
    }
  }

  // Reset form when resetKey changes (Cmd+Enter continue-creation)
  const prevResetKeyRef = useRef(resetKey)
  if (prevResetKeyRef.current !== resetKey) {
    prevResetKeyRef.current = resetKey
    if (mode === 'creating') {
      setTitle(''); setNotes(''); setChecked(false); setAttention(null)
      setDuration(null); setTags([]); setStart(null); setDeadline(null)
    }
  }

  // ── Optimistic title ──
  const optimisticTitleRef = useRef<string | null>(null)
  if (optimisticTitleRef.current !== null && task?.title === optimisticTitleRef.current) {
    optimisticTitleRef.current = null
  }

  // ── Submit logic ──
  const handleSubmit = useCallback(() => {
    const trimmed = title.trim()
    if (!trimmed) { onCancel?.(); return }

    const data: TaskCreationData = {
      title: trimmed,
      description: notes.trim() || null,
      attention,
      estimatedDuration: duration?.minutes ?? null,
      durationMode: duration?.mode ?? null,
      startDate: start?.date ?? null,
      startTime: start?.time ?? null,
      startPeriod: start?.period ?? null,
      dueDate: deadline?.date ?? null,
      dueTime: deadline?.time ?? null,
      duePeriod: deadline?.period ?? null,
      tags,
    }

    if (mode === 'creating') {
      onSubmitCreate?.(data)
    } else if (mode === 'expanded' && task) {
      const orig = {
        description: task.description ?? null, attention: task.attention ?? null,
        estimatedDuration: task.estimatedDuration ?? null, durationMode: task.durationMode ?? null,
        tags: task.tags ?? [], startDate: task.startDate ?? null, startTime: task.startTime ?? null,
        startPeriod: task.startPeriod ?? null, dueDate: task.dueDate ?? null,
        dueTime: task.dueTime ?? null, duePeriod: task.duePeriod ?? null,
      }
      if (
        trimmed === task.title && data.description === orig.description
        && attention === orig.attention && data.estimatedDuration === orig.estimatedDuration
        && data.durationMode === orig.durationMode && JSON.stringify(tags) === JSON.stringify(orig.tags)
        && data.startDate?.getTime() === orig.startDate?.getTime() && data.startTime === orig.startTime
        && data.startPeriod === orig.startPeriod && data.dueDate?.getTime() === orig.dueDate?.getTime()
        && data.dueTime === orig.dueTime && data.duePeriod === orig.duePeriod
      ) { onCancel?.(); return }
      optimisticTitleRef.current = trimmed
      onSubmitEdit?.(task.id, data)
    }
  }, [mode, task, title, notes, attention, duration, deadline, tags, onSubmitCreate, onSubmitEdit, onCancel, start])

  const handleSubmitAndCreateNew = useCallback(() => {
    const trimmed = title.trim()
    if (!trimmed) return

    const data: TaskCreationData = {
      title: trimmed,
      description: notes.trim() || null,
      attention,
      estimatedDuration: duration?.minutes ?? null,
      durationMode: duration?.mode ?? null,
      startDate: start?.date ?? null,
      startTime: start?.time ?? null,
      startPeriod: start?.period ?? null,
      dueDate: deadline?.date ?? null,
      dueTime: deadline?.time ?? null,
      duePeriod: deadline?.period ?? null,
      tags,
    }

    if (mode === 'creating') {
      onSubmitCreateAndContinue?.(data)
    } else if (mode === 'expanded' && task) {
      const orig = {
        description: task.description ?? null, attention: task.attention ?? null,
        estimatedDuration: task.estimatedDuration ?? null, durationMode: task.durationMode ?? null,
        tags: task.tags ?? [], startDate: task.startDate ?? null, startTime: task.startTime ?? null,
        startPeriod: task.startPeriod ?? null, dueDate: task.dueDate ?? null,
        dueTime: task.dueTime ?? null, duePeriod: task.duePeriod ?? null,
      }
      if (
        trimmed !== task.title || data.description !== orig.description
        || attention !== orig.attention || data.estimatedDuration !== orig.estimatedDuration
        || data.durationMode !== orig.durationMode || JSON.stringify(tags) !== JSON.stringify(orig.tags)
        || data.startDate?.getTime() !== orig.startDate?.getTime() || data.startTime !== orig.startTime
        || data.startPeriod !== orig.startPeriod || data.dueDate?.getTime() !== orig.dueDate?.getTime()
        || data.dueTime !== orig.dueTime || data.duePeriod !== orig.duePeriod
      ) { optimisticTitleRef.current = trimmed }
      onSubmitEditAndCreateBelow?.(task.id, data)
    }
  }, [mode, task, title, notes, attention, duration, deadline, tags, start, onSubmitCreateAndContinue, onSubmitEditAndCreateBelow])

  // Stable ref for pointerdown listener
  const handleSubmitRef = useRef<(() => void) | null>(null)
  handleSubmitRef.current = handleSubmit

  return {
    // Form state
    title, setTitle, notes, setNotes, checked, setChecked,
    attention, setAttention, duration, setDuration,
    tags, setTags, start, setStart, deadline, setDeadline,
    // Handlers
    handleAttentionChange, handleDurationChange, handleTagsChange,
    handleStartChange, handleDeadlineChange,
    saveToDb, saveCollapsedToDb, selectOnDropdownOpen,
    // Submit
    handleSubmit, handleSubmitAndCreateNew, handleSubmitRef, optimisticTitleRef,
    // Sounds
    playSoftRemove, playSoftAdd,
    // Derived
    isExpanded,
  }
}
